import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Event as RouterEvent, NavigationEnd, NavigationError, Router } from '@angular/router';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { EVENT_ERROR, OktaAuth } from '@okta/okta-auth-js';
import { AuthenticationService } from '@tylertech/cj-jpp-ui-core/authentication';
import { SessionTimeoutService, SessionTimeoutStatus } from '@tylertech/cj-jpp-ui-core/session-timeout';
import { Subscription } from 'rxjs/internal/Subscription';
import { FullScreenLoader, LoaderOptions, LoaderService } from '@tylertech/cj-jpp-ui-core';
import { preloadOfflineImages } from "./utils/image.utils";
import { ErrorPageService, IErrorRoutingState } from "./modules/shared/services/error-page.service";
import { ToastService } from "@tylertech/forge-angular";
import { environment } from "../environments/environment";
import { InstanceManagerService } from "./core/services/instance-manager.service";
import { UserService } from "./core/services/user.service";
import { firstValueFrom } from "rxjs";
import { TimsTokenService } from "./core/services/tims-token.service";
import { InstanceConfigService } from './core/services/instance-config.service';
import { ChromelessService } from './core/services/chromeless.service';
import { OutageStatusService } from './services/outage-status.service';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private adminWindow: Window | null = null;
  public errorRoutingState?: IErrorRoutingState;
  public isAuthenticated: boolean | undefined = false;
  public isDrawerOpen: boolean = false;
  public isUserMenuItemEnabled: boolean = false;
  public jppAdminSiteUrl: URL | undefined;
  public chromeless$ = this.chromelessService.chromeless$;
  private subscriptions: Subscription[] = [];
  public showErrorPage: boolean = false;
  private _sessionTimeoutSubscription?: Subscription;
  private fullScreenLoader: FullScreenLoader;
  private dialogBodyObserver?: MutationObserver;
  private dialogOpenListener?: (e: Event) => void;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private router: Router,
    private authStateService: OktaAuthStateService,
    private authService: AuthenticationService,
    private instanceManagerService: InstanceManagerService,
    private loaderService: LoaderService,
    private sessionTimeoutService: SessionTimeoutService,
    private errorPageService: ErrorPageService,
    private toastService: ToastService,
    protected userService: UserService,
    private jitTokenService: TimsTokenService,
    private instanceConfigService: InstanceConfigService,
    private chromelessService: ChromelessService,
    public outageStatusService: OutageStatusService
  ) {
    this.oktaAuth.tokenManager.on(EVENT_ERROR, async (err) => {
      await this.oktaAuth.signInWithRedirect();
    });

    const options = new LoaderOptions();
    options.message = 'Validating user session...';
    options.hasBackdrop = true;
    options.panelClass = 'default-site-busy-indicator';

    this.fullScreenLoader = this.loaderService.createAndShowFullScreenLoader(options);
  }

  ngOnInit() {
    this.configureAuthListeners();
    this.subscriptions.push(
      this.router.events.subscribe(e => this.handleNavigationEvent(e))
    );
    this.subscriptions.push(
      this.errorPageService.showErrorPageSubject.subscribe(e => this.onShowErrorPage(e))
    );
    // Auto-close drawer whenever we enter a chromeless route
    this.subscriptions.push(
      this.chromeless$.subscribe(isChromeless => {
        if (isChromeless && this.isDrawerOpen) {
          this.isDrawerOpen = false;
        }
      })
    );
    preloadOfflineImages();
    this.outageStatusService.startRemotePolling();
    this.initDialogAriaFixer();
  }

  ngOnDestroy(): void {
    if (this.dialogBodyObserver) {
      this.dialogBodyObserver.disconnect();
    }
    if (this.dialogOpenListener && typeof document !== 'undefined' && document.body) {
      document.body.removeEventListener('forge-dialog-open', this.dialogOpenListener, true);
    }
    this._sessionTimeoutSubscription?.unsubscribe();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initDialogAriaFixer(): void {
    if (typeof document === 'undefined' || !document.body) return;

    const fixDialog = (dialogEl: Element) => {
      dialogEl.removeAttribute('aria-labelledby');
      dialogEl.removeAttribute('aria-describedby');

      const shadow = dialogEl.shadowRoot;
      if (!shadow) return;

      const nativeDialog = shadow.querySelector('dialog');
      if (nativeDialog) {
        nativeDialog.removeAttribute('aria-labelledby');
        nativeDialog.removeAttribute('aria-describedby');

        let labelText = dialogEl.getAttribute('label')
          || (dialogEl as { label?: string }).label
          || shadow.querySelector('#forge-dialog-label')?.textContent
          || dialogEl.querySelector('h1, h2, h3, .modal-title, [slot="start"], [slot="header"]')?.textContent
          || 'Dialog window';

        nativeDialog.setAttribute('aria-label', labelText.trim() || 'Dialog window');

        const descriptionText = (dialogEl as { description?: string }).description
          || shadow.querySelector('#forge-dialog-description')?.textContent
          || '';

        if (descriptionText.trim()) {
          nativeDialog.setAttribute('aria-description', descriptionText.trim());
        } else {
          nativeDialog.removeAttribute('aria-description');
        }
      }
    };

    this.dialogOpenListener = (e: Event) => {
      if (e.target && (e.target as Element).matches?.('forge-dialog')) {
        fixDialog(e.target as Element);
      }
    };
    document.body.addEventListener('forge-dialog-open', this.dialogOpenListener, true);

    this.dialogBodyObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            const el = node as Element;
            if (el.matches?.('forge-dialog')) fixDialog(el);
            el.querySelectorAll?.('forge-dialog')?.forEach(d => fixDialog(d));
          }
        });
      }
    });

    this.dialogBodyObserver.observe(document.body, { childList: true, subtree: true });

    document.querySelectorAll('forge-dialog').forEach(d => fixDialog(d));
  }

  public handleMenuButtonClick(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
  }

  public handleUserMenuItemClick() {
    if (this.adminWindow != null && !this.adminWindow.closed) {
      this.adminWindow.focus();
    } else {
      this.adminWindow = window.open(this.jppAdminSiteUrl?.href, '_blank', 'popup');
    }
  }

  public isRouteActive(route: string): boolean {
    return this.router.url.includes(route);
  }

  private async setIsUserMenuItemEnabled() {
    if (!await firstValueFrom(this.userService.hasInstanceAdminRole$)) {
      this.isUserMenuItemEnabled = false;
      return;
    }

    if (!environment.jppAdminBaseUrl) {
      this.isUserMenuItemEnabled = false;
      return;
    }

    let baseURl = environment.jppAdminBaseUrl;
    baseURl = baseURl.endsWith('/') ? baseURl.slice(0, -1) : baseURl;

    this.jppAdminSiteUrl = new URL(`${baseURl}/app/applications/imageservice/${this.instanceManagerService.instanceIdSnapshot}`);
    this.isUserMenuItemEnabled = true;
  }

  private configureAuthListeners() {
    this.subscriptions.push(this.jitTokenService.tokenValidated$.subscribe({
      next: async value => this.fullScreenLoader.hideAll()
    }))

    this.subscriptions.push(
      this.authStateService.authState$.subscribe(async authState => {
        if (authState.isAuthenticated) {
          await this.instanceManagerService.initWithActiveInstance();
        }

        this.isAuthenticated = authState.isAuthenticated;
        await this.setIsUserMenuItemEnabled();

        this.toggleSessionTimeout();
        this.fullScreenLoader.hideAll();
      })
    );

    this.oktaAuth.tokenManager.on(EVENT_ERROR, async (err) => {
      await this.authService.signIn();
    });
  }

  private handleNavigationEvent(event: RouterEvent) {
    if (event instanceof NavigationEnd) {
      this.showErrorPage = false;
      return;
    }

    if (event instanceof NavigationError && (event.error as { name?: string })?.name === 'ChunkLoadError') {
      this.errorPageService.showErrorPage(ErrorPageService.Offline);
    }
  }

  private toggleSessionTimeout() {
    if (this.isAuthenticated) {
      this._sessionTimeoutSubscription?.unsubscribe();

      this._sessionTimeoutSubscription = this.sessionTimeoutService.sessionTimeout$.subscribe(async status => {
        if (status === SessionTimeoutStatus.COMPLETE) {
          await this.authService.logOut();
        }
      });

      this.sessionTimeoutService.startSessionMonitor();
    } else {
      this._sessionTimeoutSubscription?.unsubscribe();
      this.sessionTimeoutService.reset();
    }
  }

  private onShowErrorPage(state: IErrorRoutingState) {
    this.errorRoutingState = state;
    this.showErrorPage = true;
  }

  get outage$() { return this.outageStatusService.outage$; }
}